import {globalEvent} from '@shared/easyfy.core';
import {invoke} from '@shared/easyfy.decorators';
import { hide, parseToBoolean, show, isMobile, addCss, removeCss, debounce } from '@shared/easyfy.utils';
import searchService from '@webServices/search.service';
export class SearchConfig {

  constructor(
      thresholdVolume: number = 3,
      showPopularResultsOnFocusIn: boolean = false,
      showCampaignsOnFocusIn: boolean = false,
      keepPopularResultsOnSearch: boolean = false,
      keepCampaignsOnSearch: boolean = false,
      showPopularResultsIfNoResults: boolean = false,
      displayStyle: string = "block",
  ) {

    this.ThresholdVolume = thresholdVolume;
    this.ShowPopularResultsOnFocusIn = showPopularResultsOnFocusIn;
    this.ShowCampaignsOnFocusIn = showCampaignsOnFocusIn;
    this.KeepPopularResultsOnSearch = keepPopularResultsOnSearch;
    this.KeepCampaignsOnSearch = keepCampaignsOnSearch;
    this.ShowPopularResultsIfNoResults = showPopularResultsIfNoResults;
    this.DisplayStyle = displayStyle;
  }

  public ThresholdVolume: number;
  public ShowPopularResultsOnFocusIn: boolean;
  public ShowCampaignsOnFocusIn: boolean;
  public KeepPopularResultsOnSearch: boolean;
  public KeepCampaignsOnSearch: boolean;
  public ShowPopularResultsIfNoResults: boolean;
  public DisplayStyle: string;
}

namespace Easyfy.Modules {

  @invoke
  export class Search {
    static Instance: Search;

    private backdrop: HTMLElement;
    private searchForm: HTMLElement;
    private searchInput: HTMLInputElement;
    private searchResults: HTMLElement;
    private searchBarElement: HTMLElement;
    private searchIcon: HTMLElement;
    private campaignResults: HTMLElement = null;
    private popularResults: HTMLElement = null;

    private pendingSearch: string;
    private focusInCampaignData: string = null;
    private focusInPopularData: string = null;

    private initialized: boolean;

    private thresholdVolume: number;
    private showPopularResultsOnFocusIn: boolean;
    private showCampaignsOnFocusIn: boolean;
    public keepPopularResultsOnSearch: boolean;
    public keepCampaignProductsOnSearch: boolean;
    public showPopularResultsIfNoResults: boolean;
    public displayStyle: string;

    constructor(config: SearchConfig) {
      Search.Instance = this;

      if (config != null) {
        Search.Instance.thresholdVolume = config.ThresholdVolume;
        Search.Instance.showPopularResultsOnFocusIn = config.ShowPopularResultsOnFocusIn;
        Search.Instance.showCampaignsOnFocusIn = config.ShowCampaignsOnFocusIn;
        Search.Instance.keepPopularResultsOnSearch = config.KeepPopularResultsOnSearch;
        Search.Instance.keepCampaignProductsOnSearch = config.KeepCampaignsOnSearch;
        Search.Instance.showPopularResultsIfNoResults = config.ShowPopularResultsIfNoResults;
        Search.Instance.displayStyle = config.DisplayStyle;
      }
    }

    OnInit() {
      
      globalEvent({
        type: 'focusin',
        event: {
          action: 'search-input',
          callback: Search.onSearch
        }
      });

      globalEvent({
        type: 'keyup',
        event: {
          action: 'search-input',
          callback: debounce(Search.onSearch, 500)
        }
      });

      globalEvent({
        type: 'click',
        event: {
          event: 'search-close',
          callback: Search.CloseSearch
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'search-suggestion',
          callback: Search.onSearchSuggestion
        }
      });

      if (Search.Instance.showPopularResultsOnFocusIn || Search.Instance.showCampaignsOnFocusIn){
        globalEvent({
          type: 'click',
          event: {
            action: 'search-open',
            callback: Search.onSearchOpen
          }
        });
      }
    }
    private static onSearchOpen = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // trigger click
      const el = e.target as HTMLElement;
      const fromEl = el?.parentElement as HTMLElement;
      const searchInputElement = fromEl?.querySelector('[data-action="search-input"]') as HTMLInputElement;
      
      if (!searchInputElement) return false;
      await Search.initNewSearchInstance(searchInputElement);
      Search.Instance.initialized = true;
      searchInputElement.focus();

      return false;
    }

    private static onSearch = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.type === 'focusin') {
        await Search.initNewSearchInstance(e.target as HTMLInputElement);
        Search.Instance.initialized = true;
      }

      if (e.key === "Escape"){
        Search.CloseSearch(e);
      } else if (e.type === 'focusin' || e.type === 'keyup') {

        Search.Instance.ShowBackdrop(null);
        show(Search.Instance.searchIcon);
        const value: string = Search.Instance.searchInput.value.trim() ?? '';

        //prevents multiple requests from different events for same query
        if (Search.Instance.pendingSearch !== value?.trim()) {
          Search.Instance.pendingSearch = value?.trim();

          const targetAttrs = e.target.attributes;
          const token = targetAttrs['data-token']?.value;

          if (Search.ShouldSearchBasedOnUserInput()) {

            await Search.GetSearchDataBasedOnUserInput(targetAttrs, token);

            if (!Search.Instance.keepPopularResultsOnSearch && Search.Instance.popularResults != null){
              Search.Instance.popularResults.style.display = 'none';
            }

            if (!Search.Instance.keepCampaignProductsOnSearch && Search.Instance.popularResults != null){
              Search.Instance.popularResults.style.display = 'none';
            }

          } else if (Search.ShouldGetFocusInData()) {

            if (!Search.Instance.keepPopularResultsOnSearch && Search.Instance.popularResults != null){
              Search.Instance.popularResults.style.display = 'none';
            }

            if (!Search.Instance.keepCampaignProductsOnSearch && Search.Instance.popularResults != null){
              Search.Instance.popularResults.style.display = 'none';
            }

            await Search.GetFocusInData(token);

          } else if (!Search.Instance.initialized) {

            Search.Instance.SearchClear();

          }

          if (Search.ShouldHideSearchBasedOnUserInput()){
            Search.Instance.searchResults.style.display = 'none';
          }
        }
      }

      return false;
    }

    private static onSearchSuggestion = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const element: HTMLElement = e.target as HTMLElement;
      const newValue = element.attributes['data-value']?.value;

      Search.Instance.searchInput.value = newValue;
      Search.Instance.searchInput.focus();

      return false;
    }

    private static ShouldSearchBasedOnUserInput() {
      return Search.Instance.pendingSearch != null && Search.Instance.pendingSearch?.length >= Search.Instance.thresholdVolume;
    }

    private static ShouldHideSearchBasedOnUserInput() {
      return Search.Instance.pendingSearch != null && Search.Instance.pendingSearch?.length < Search.Instance.thresholdVolume;
    }

    private static ShouldGetFocusInData() {
      return (Search.Instance.showPopularResultsOnFocusIn || Search.Instance.showCampaignsOnFocusIn)
          && Search.Instance.pendingSearch != null && Search.Instance.pendingSearch?.length < Search.Instance.thresholdVolume;
    }

    private static GetSearchDataBasedOnUserInput = async (targetAttrs: any, token: any) => {
      const searchData = {
        includeArticles: parseToBoolean(targetAttrs['data-include-articles']?.value),
        includeBrands: parseToBoolean(targetAttrs['data-include-brands']?.value),
        includePages: parseToBoolean(targetAttrs['data-include-pages']?.value),
        searchTerm: Search.Instance.pendingSearch
      };

      searchService.Search(searchData, token).then((response) => {
        if (Search.Instance.pendingSearch == response.data.query) {
          Search.Instance.pendingSearch = null;
        }

        if (response.data.success) {
          //ignore result if input was emptied during fetch of data
          if (Search.Instance.searchInput.value.trim()) {
            Search.Instance.searchResults.innerHTML = response.data.partial;
            show(Search.Instance.searchResults, Search.Instance.displayStyle);
            
            var noResultsContainer = document.querySelector('[data-container="no-results"]');
            if (noResultsContainer != null && Search.Instance.showPopularResultsIfNoResults && Search.Instance.popularResults != null) {
               Search.Instance.popularResults.style.display = Search.Instance.displayStyle;
            } else if (Search.Instance.popularResults != null) {
              Search.Instance.popularResults.style.display = 'none';
            }
          }
        }
      });
    }

    private static GetFocusInData = async (token: any) => {

      // don't get focusIn data from server if we already have it in this instance
      if (Search.Instance.focusInCampaignData != null || Search.Instance.focusInPopularData != null) {

        Search.Instance.campaignResults != null ? Search.Instance.campaignResults.innerHTML = Search.Instance.focusInCampaignData : null;
        Search.Instance.campaignResults != null ? Search.Instance.campaignResults.style.display = Search.Instance.displayStyle : false;
        
        Search.Instance.popularResults != null ? Search.Instance.popularResults.innerHTML = Search.Instance.focusInPopularData : null;
        Search.Instance.popularResults != null ? Search.Instance.popularResults.style.display = Search.Instance.displayStyle : false;

        return;
      }

      const focusInParams = {
        GetPopularResults: Search.Instance.showPopularResultsOnFocusIn,
        GetCampaignResults: Search.Instance.showCampaignsOnFocusIn
      }

      searchService.GetFocusInData(focusInParams, token).then((response) => {

        if (Search.Instance.pendingSearch == response.data.query) {
          Search.Instance.pendingSearch = null;
        }

        if (response.data.success) {

          Search.Instance.campaignResults.innerHTML = response.data.partialCampaignResults;
          Search.Instance.focusInCampaignData = response.data.partialCampaignResults;

          Search.Instance.popularResults.innerHTML = response.data.partialPopularResults;
          Search.Instance.focusInPopularData = response.data.partialPopularResults;

          if (Search.Instance.popularResults.innerHTML !== ""){
            Search.Instance.popularResults.style.display = Search.Instance.displayStyle;
          }

          if (Search.Instance.campaignResults.innerHTML !== ""){
            Search.Instance.campaignResults.style.display = Search.Instance.displayStyle;
          }else{
            Search.Instance.campaignResults.style.display = 'none';
          }
        }
      });
    }

    private static initNewSearchInstance = async (searchInputElement: HTMLInputElement) => {
      Search.Instance.searchBarElement = searchInputElement.closest('[data-container="search-bar"]');
      Search.Instance.searchBarElement.classList.add('active');
      Search.Instance.searchInput = searchInputElement;
      Search.Instance.searchResults = Search.Instance.searchBarElement.querySelector('[data-container="search-result"]') as HTMLElement;
      Search.Instance.campaignResults = Search.Instance.searchBarElement.querySelector('[data-container="campaign-results"]') as HTMLElement;
      Search.Instance.popularResults = Search.Instance.searchBarElement.querySelector('[data-container="popular-results"]') as HTMLElement;
      Search.Instance.backdrop = document.querySelector('header').querySelector('[data-container="search-backdrop"]') as HTMLElement;
      Search.Instance.backdrop.classList.add('active');
      Search.Instance.searchIcon = Search.Instance.searchBarElement.querySelector('[data-event="search-close"]') as HTMLElement;
      Search.Instance.searchForm = Search.Instance.searchBarElement.querySelector('form');
      
      if (Search.Instance.backdrop != null && isMobile()) {
        document.body.style.overflow = 'hidden';
        addCss(document.body, 'noof')
      }

      Search.preventEmptySearch();
      Search.hideSearchEventListener();
    }

    private static hideSearchEventListener = () => {
      const backdrop = Search.Instance.backdrop;
      backdrop.addEventListener('click', (event: any) => {
        if (!backdrop.contains(event.target) || (event.target.hasAttribute('data-container') && event.target.attributes['data-container']?.value == 'search-backdrop')) {
          Search.Instance.SearchClear();
          document.body.style.overflow = 'auto';
        }
      }, {once: true});
    }

    private static CloseSearch(e) {
      e.preventDefault();
      e.stopPropagation();

      Search.Instance.SearchClear();

      return false;
    }

    private static preventEmptySearch = () => {
      Search.Instance.searchForm?.addEventListener('submit', (e) => {
        if (!Search.Instance.searchInput.value) {
          e.preventDefault();
          return;
        };
      })
    }

    SearchClear() {
      Search.Instance.HideSearchResult(null);
      Search.Instance.HideBackdrop(null);
      Search.Instance.searchInput.blur();
      Search.Instance.searchInput.value = "";
      document.body.style.overflow = 'auto';
      removeCss(document.body, 'noof');

      if (Search.Instance.searchIcon != null) {
        hide(Search.Instance.searchIcon);
      }
    }

    SearchBarInFocus(e) {
      if (e.type === 'focusin') {
        if (!Search.Instance.IsEventTargetInsideSearch(e) && Search.Instance.searchResults != null) {
          hide(Search.Instance.searchResults);
        }
      }
    }

    HideSearchResult(e: any) {
      e?.target ? (!Search.Instance.IsEventTargetInsideSearch(e) ? hide(Search.Instance.searchResults) : '') : hide(Search.Instance.searchResults);
      Search.Instance.searchBarElement.classList.remove('active');
    }

    ShowSearchResult(e: any) {
      e?.target ? (!Search.Instance.IsEventTargetInsideSearch(e) ? show(Search.Instance.searchResults, Search.Instance.displayStyle) : '') : show(Search.Instance.searchResults, Search.Instance.displayStyle);
    }

    HideBackdrop(e: any) {
      e?.target ? (!Search.Instance.IsEventTargetInsideSearch(e) ? Search.Instance.backdrop.classList.add('active') : '') : Search.Instance.backdrop.classList.remove('active');
    }

    ShowBackdrop(e: any) {
      if (Search.Instance.backdrop)
        e?.target ? (!Search.Instance.IsEventTargetInsideSearch(e) ? Search.Instance.backdrop.classList.remove('active') : '') : Search.Instance.backdrop.classList.add('active');
    }

    IsEventTargetInsideSearch(e: any) {
      if (this.searchBarElement == null) {
        return false;
      }

      return this.searchBarElement.contains(e.target);
    }
  }
}

export default Easyfy.Modules;